import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { genomeApi } from '../api/genomeApi'
import { useToast } from '@/hooks/use-toast'

export const genomeKeys = { status: ['easel', 'genome', 'status'] as const }

/**
 * The artist's genome status, polled only while something is actually running.
 *
 * Derivation takes tens of seconds (measured: 24-37s of image fetching alone, then a model call),
 * so a fixed interval would either be too slow to feel responsive or waste requests forever. The
 * interval is live only in the `pending` state.
 */
export function useGenomeStatus(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: genomeKeys.status,
    queryFn: genomeApi.status,
    enabled: options.enabled ?? true,
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? 5000 : false),
  })
}

export function useGenerateGenome() {
  const qc = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: genomeApi.generate,
    onSuccess: (data) => {
      qc.setQueryData(genomeKeys.status, data)
      qc.invalidateQueries({ queryKey: genomeKeys.status })
      toast({
        title: 'Reading your work',
        description:
          'Your genome is being derived from your own images and words. This takes a minute or two.',
      })
    },
    onError: (error: any) => {
      const detail = error?.response?.data
      // The API returns the whole eligibility state on a refusal, so the message can say what to
      // do rather than that something was not allowed.
      toast({
        title: 'Not yet',
        description: detail?.message ?? 'Could not start deriving your genome.',
        variant: 'destructive',
      })
    },
  })
}
